import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AppShell from "./components/AppShell";
import Home from "./features/home/Home";
import SectionMenu from "./components/SectionMenu";
import SignIn from "./features/auth/SignIn";
import PendingApproval from "./features/auth/PendingApproval";
import { useSession } from "./lib/session";
import { Loading } from "./components/ui";

// Public display routes render for anyone, session or not — no AppShell,
// no auth gate. Kept as their own lazy imports so a kiosk's first paint
// never pulls in the whole authenticated app bundle.
const PublicDisplay = lazy(() => import("./features/kpi-dashboards/display/PublicDisplay"));

/* Lazy like every other page. Its own data is already in cache before
   anyone taps it -- AppShell's badge runs the same four queries on every
   screen -- so the only thing loading here is the chunk. */
const ToDo          = lazy(() => import("./features/todo/ToDo"));
const FleetList     = lazy(() => import("./features/vehicles/FleetList"));
const FleetMap      = lazy(() => import("./features/vehicles/FleetMap"));
const VehicleDetail = lazy(() => import("./features/vehicles/VehicleDetail"));
const PoolBooking   = lazy(() => import("./features/vehicles/PoolBooking"));
const FleetCompliance = lazy(() => import("./features/vehicles/FleetCompliance"));
const Issues        = lazy(() =>
  import("./features/vehicles/Issues").then((m) => ({ default: m.VehicleIssues })),
);
const ReportIssue   = lazy(() =>
  import("./features/vehicles/Issues").then((m) => ({ default: m.ReportIssue })),
);
const Accident      = lazy(() =>
  import("./features/vehicles/Issues").then((m) => ({ default: m.AccidentEmergency })),
);

const LeadsList  = lazy(() => import("./features/leads/LeadsList"));
const LeadDetail = lazy(() => import("./features/leads/LeadDetail"));

const Pipeline       = lazy(() => import("./features/quotes/Pipeline"));
const AllQuotes      = lazy(() => import("./features/quotes/AllQuotes"));
const QuoteWizard    = lazy(() => import("./features/quotes/QuoteWizard"));
const BudgetImport   = lazy(() => import("./features/quotes/budgetImport/BudgetImportReview"));
const QuoteDetail    = lazy(() => import("./features/quotes/QuoteDetail"));
const QuotesSettings = lazy(() => import("./features/quotes/QuotesSettings"));

const PeopleList     = lazy(() => import("./features/people/PeopleList"));
const PersonDetail   = lazy(() => import("./features/people/PersonDetail"));
const ComplianceList = lazy(() => import("./features/people/ComplianceList"));
const InsuranceList  = lazy(() => import("./features/insurance/InsuranceList"));

const CompanyList   = lazy(() => import("./features/customers/CompanyList"));
const CompanyDetail = lazy(() => import("./features/customers/CompanyDetail"));

const ServiceDashboard = lazy(() => import("./features/service/Dashboard"));
const Tickets           = lazy(() => import("./features/service/Tickets"));
const TicketDetail      = lazy(() => import("./features/service/TicketDetail"));
const TicketStats       = lazy(() => import("./features/service/TicketStats"));
const Visits            = lazy(() => import("./features/service/Visits"));
const PmBoard           = lazy(() => import("./features/service/PmBoard"));
const ServiceIssues     = lazy(() => import("./features/service/Issues"));
const VisitDetail       = lazy(() => import("./features/service/VisitDetail"));
const RoomsEquipment    = lazy(() => import("./features/service/RoomsEquipment"));
const RoomDetail        = lazy(() => import("./features/service/RoomDetail"));
const Contracts         = lazy(() => import("./features/service/Contracts"));
const RecurringServices = lazy(() => import("./features/service/RecurringServices"));
const RecurringServiceDetail = lazy(() => import("./features/service/RecurringServiceDetail"));
const ContractDetail    = lazy(() => import("./features/service/ContractDetail"));
const ClientOffices     = lazy(() => import("./features/service/ClientOffices"));
const SiteDetail        = lazy(() => import("./features/service/SiteDetail"));
const PhoneLog          = lazy(() => import("./features/service/PhoneLog"));
const RenewalQuotes     = lazy(() => import("./features/service/RenewalQuotes"));
const ClientActivityReport = lazy(() => import("./features/service/ClientActivityReport"));
const EquipmentCatalogue   = lazy(() => import("./features/service/EquipmentCatalogue"));
const Manufacturers        = lazy(() => import("./features/service/Manufacturers"));
const EquipmentModels      = lazy(() => import("./features/service/EquipmentModels"));

const FinanceLedger = lazy(() => import("./features/finance/FinanceLedger"));
const FinanceImport = lazy(() => import("./features/finance/FinanceImport"));

const StockDashboard       = lazy(() => import("./features/stock/StockDashboard"));
const StockList            = lazy(() => import("./features/stock/StockList"));
const StockItemDetail      = lazy(() => import("./features/stock/StockItemDetail"));
const StockLoans           = lazy(() => import("./features/stock/StockLoans"));
const StockMovementActions = lazy(() => import("./features/stock/StockMovementActions"));

const JobsList   = lazy(() => import("./features/wip/JobsList"));
const JobDetail  = lazy(() => import("./features/wip/JobDetail"));
const Planner    = lazy(() => import("./features/wip/Planner"));
const MySchedule = lazy(() => import("./features/wip/MySchedule"));
const ProjectAnalysis = lazy(() => import("./features/wip/ProjectAnalysis"));

const CommissioningList       = lazy(() => import("./features/commissioning/CommissioningList"));
const CommissioningJobDetail  = lazy(() => import("./features/commissioning/CommissioningJobDetail"));
const CommissioningSettings   = lazy(() => import("./features/commissioning/CommissioningSettings"));

const ProductLibrary       = lazy(() => import("./features/products/ProductLibrary"));
const ProductProfileDetail = lazy(() => import("./features/products/ProductProfileDetail"));

const SupabaseUsage = lazy(() => import("./features/admin/SupabaseUsage"));
const ClaudeUsage = lazy(() => import("./features/admin/ClaudeUsage"));
const SupportTicketing = lazy(() => import("./features/platform/SupportTicketing"));
const Integrations     = lazy(() => import("./features/platform/Integrations"));
const SystemHealth     = lazy(() => import("./features/platform/SystemHealth"));
const AccessLog        = lazy(() => import("./features/platform/AccessLog"));

const KpiDashboardList = lazy(() => import("./features/kpi-dashboards/DashboardList"));
const KpiDashboardEditor = lazy(() => import("./features/kpi-dashboards/DashboardEditor"));
const KpiDashboardPreview = lazy(() => import("./features/kpi-dashboards/DashboardPreview"));
const KpiViewLibrary = lazy(() => import("./features/kpi-dashboards/ViewLibrary"));
const KpiTrustedNetworks = lazy(() => import("./features/kpi-dashboards/TrustedNetworks"));
const KpiDisplayKeys = lazy(() => import("./features/kpi-dashboards/DisplayKeys"));

const WeeklyChecks        = lazy(() => import("./features/hs/WeeklyChecks"));
const WeeklyCheckForm     = lazy(() => import("./features/hs/WeeklyCheckForm"));
const WeeklyCheckDetail   = lazy(() => import("./features/hs/WeeklyCheckDetail"));
const AnnualServices      = lazy(() => import("./features/hs/AnnualServices"));
const AnnualServiceDetail = lazy(() => import("./features/hs/AnnualServiceDetail"));
const HSComplianceReport  = lazy(() => import("./features/hs/ComplianceReport"));
const HSCheckSettings     = lazy(() => import("./features/hs/CheckSettings"));

export default function App() {
  const { session, profile, loading } = useSession();

  // Public kiosk displays short-circuit every auth gate below, the same
  // way SignIn/PendingApproval already do -- a display can't wait on a
  // session that will never exist, and must never render app chrome.
  // Access itself is enforced server-side by public.kpi_get_dashboard
  // (IP allowlist / display-key validation), not by this route existing.
  if (window.location.pathname.startsWith("/display/")) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/display/key/:token" element={<PublicDisplay mode="key" />} />
          <Route path="/display/:slug" element={<PublicDisplay mode="slug" />} />
        </Routes>
      </Suspense>
    );
  }

  if (loading) return <Loading rows={3} />;
  if (!session) return <SignIn />;

  // Signed in but not approved: every new sign-in starts inactive with no
  // role, and RLS returns nothing for them. This screen says so instead of
  // rendering empty pages. Approval is an administrator's act in the
  // database — nothing here grants anything.
  if (!profile || !profile.is_active) return <PendingApproval />;

  return (
    <Suspense fallback={<Loading rows={5} />}>
      <Routes>
        <Route element={<AppShell />}>
          {/* To Do — everything outstanding, from the same
              permission-filtered RPCs the home screen used to show as
              four separate cards. */}
          <Route path="/todo" element={<ToDo />} />

          {/* Sales / Projects — grouping landings. Their member modules
              keep their own routes and their own landings underneath. */}
          <Route path="/sales" element={<SectionMenu section="sales" title="Sales" />} />
          <Route path="/projects" element={<SectionMenu section="projects" title="Projects" />} />

          <Route path="/vehicles" element={<SectionMenu section="vehicles" title="Vehicles" />} />
          <Route path="/vehicles/fleet" element={<FleetMap />} />
          <Route path="/vehicles/list" element={<FleetList />} />
          <Route path="/vehicles/compliance" element={<FleetCompliance />} />
          <Route path="/vehicles/pool-booking" element={<PoolBooking />} />
          <Route path="/vehicles/issues" element={<Issues />} />
          <Route path="/vehicles/report-issue" element={<ReportIssue />} />
          <Route path="/vehicles/accident" element={<Accident />} />
          <Route path="/vehicles/:id" element={<VehicleDetail />} />

          {/* Leads — visibility is presentation only; RLS + can_view_quotes/can_manage_quotes is the boundary */}
          <Route path="/leads" element={<LeadsList />} />
          <Route path="/leads/:id" element={<LeadDetail />} />

          {/* Quotes — visibility is presentation only; RLS is the boundary */}
          <Route path="/quotes" element={<SectionMenu section="quotes" title="Quotes" parent={{ label: "Sales", to: "/sales" }} />} />
          <Route path="/quotes/pipeline" element={<Pipeline />} />
          <Route path="/quotes/all" element={<AllQuotes />} />
          <Route path="/quotes/new" element={<QuoteWizard />} />
          <Route path="/quotes/import" element={<BudgetImport />} />
          <Route path="/quotes/settings" element={<QuotesSettings />} />
          <Route path="/quotes/:id" element={<QuoteDetail />} />

          {/* People — visibility is presentation only; RLS is the boundary */}
          <Route path="/people" element={<SectionMenu section="people" title="People" />} />
          <Route path="/people/list" element={<PeopleList />} />
          <Route path="/people/insurance" element={<InsuranceList />} />
          <Route path="/people/compliance" element={<ComplianceList />} />
          <Route path="/people/:id" element={<PersonDetail />} />

          {/* Customers — visibility is presentation only; RLS is the boundary */}
          <Route path="/customers" element={<CompanyList />} />
          <Route path="/customers/:id" element={<CompanyDetail />} />

          {/* Service — visibility is presentation only; RLS is the boundary */}
          <Route path="/service" element={<SectionMenu section="service" title="Service" />} />
          <Route path="/service/dashboard" element={<ServiceDashboard />} />
          <Route path="/service/tickets" element={<Tickets />} />
          <Route path="/service/tickets/stats" element={<TicketStats />} />
          <Route path="/service/tickets/:id" element={<TicketDetail />} />
          <Route path="/service/visits" element={<Visits />} />
          <Route path="/service/pm" element={<PmBoard />} />
          <Route path="/service/issues" element={<ServiceIssues />} />
          <Route path="/service/visits/:id" element={<VisitDetail />} />
          <Route path="/service/customers/:id/rooms" element={<RoomsEquipment />} />
          <Route path="/service/rooms/:id" element={<RoomDetail />} />
          <Route path="/service/contracts" element={<Contracts />} />
          {/* Old separate "All Contracts" page is now the All tab of Contracts. */}
          <Route path="/service/contracts/all" element={<Navigate to="/service/contracts?scope=all" replace />} />
          <Route path="/service/recurring-services" element={<RecurringServices />} />
          <Route path="/service/recurring-services/:id" element={<RecurringServiceDetail />} />
          <Route path="/service/contracts/:id" element={<ContractDetail />} />
          <Route path="/service/sites" element={<ClientOffices />} />
          <Route path="/service/sites/:id" element={<SiteDetail />} />
          <Route path="/service/phone-log" element={<PhoneLog />} />
          <Route path="/service/renewals" element={<RenewalQuotes />} />
          <Route path="/service/activity-report" element={<ClientActivityReport />} />
          <Route path="/service/equipment" element={<EquipmentCatalogue />} />
          <Route path="/service/manufacturers" element={<Manufacturers />} />

          {/* Finance — visibility is presentation only; RLS + can_view_finance/can_manage_finance is the boundary */}
          <Route path="/finance" element={<SectionMenu section="finance" title="Finance" />} />
          <Route path="/finance/ledger" element={<FinanceLedger />} />
          <Route path="/finance/import" element={<FinanceImport />} />

          {/* Projects (internally "wip") — visibility is presentation only; RLS + can_view_wip/can_manage_wip is the boundary */}
          {/* The Projects group landing IS the projects menu -- see sectionNav. */}
          <Route path="/wip" element={<Navigate to="/projects" replace />} />
          <Route path="/wip/jobs" element={<JobsList />} />
          <Route path="/wip/jobs/:id" element={<JobDetail />} />
          <Route path="/wip/planner" element={<Planner />} />
          <Route path="/wip/my-schedule" element={<MySchedule />} />
          <Route path="/wip/analysis" element={<ProjectAnalysis />} />

          {/* Commissioning — its own department; visibility is presentation only, RLS + can_view_wip/can_manage_wip is the boundary */}
          <Route path="/commissioning" element={<CommissioningList />} />
          <Route path="/commissioning/settings" element={<CommissioningSettings />} />
          <Route path="/commissioning/jobs/:id" element={<CommissioningJobDetail />} />

          {/* Stock — visibility is presentation only; RLS + can_view_stock/can_manage_stock is the boundary */}
          <Route path="/stock" element={<SectionMenu section="stock" title="Stock" />} />
          <Route path="/stock/dashboard" element={<StockDashboard />} />
          <Route path="/stock/items" element={<StockList />} />
          <Route path="/stock/items/:id" element={<StockItemDetail />} />
          <Route path="/stock/loans" element={<StockLoans />} />
          <Route path="/stock/actions" element={<StockMovementActions />} />

          {/* Product Library — visibility is presentation only; RLS + can_view_quotes/can_view_service is the boundary */}
          <Route path="/products" element={<ProductLibrary />} />
          <Route path="/products/equipment-models" element={<EquipmentModels />} />
          <Route path="/products/:id" element={<ProductProfileDetail />} />

          {/* Health & Safety — visibility is presentation only; RLS + can_view_hs/can_manage_hs is the boundary */}
          <Route path="/hs" element={<SectionMenu section="hs" title="Health & Safety" />} />
          <Route path="/hs/weekly" element={<WeeklyChecks />} />
          <Route path="/hs/weekly/new" element={<WeeklyCheckForm />} />
          <Route path="/hs/weekly/:id" element={<WeeklyCheckDetail />} />
          <Route path="/hs/weekly/:id/edit" element={<WeeklyCheckForm />} />
          <Route path="/hs/annual" element={<AnnualServices />} />
          <Route path="/hs/annual/:id" element={<AnnualServiceDetail />} />
          <Route path="/hs/report" element={<HSComplianceReport />} />
          <Route path="/hs/settings" element={<HSCheckSettings />} />

          {/* Platform — visibility is presentation only; RLS + can_view_platform/can_view_supabase_usage is the boundary */}
          <Route path="/platform" element={<SectionMenu section="admin" title="Platform" />} />
          <Route path="/admin/supabase-usage" element={<SupabaseUsage />} />
          <Route path="/admin/claude-usage" element={<ClaudeUsage />} />
          <Route path="/platform/support-ticketing" element={<SupportTicketing />} />
          <Route path="/platform/integrations" element={<Integrations />} />
          <Route path="/platform/system-health" element={<SystemHealth />} />
          <Route path="/platform/access-log" element={<AccessLog />} />

          {/* KPI Dashboards — visibility is presentation only; RLS + can_view_kpi_dashboards/can_manage_kpi_dashboards is the boundary */}
          <Route path="/kpi" element={<KpiDashboardList />} />
          <Route path="/kpi/views" element={<KpiViewLibrary />} />
          <Route path="/kpi/trusted-networks" element={<KpiTrustedNetworks />} />
          <Route path="/kpi/display-keys" element={<KpiDisplayKeys />} />
          <Route path="/kpi/dashboards/:id/edit" element={<KpiDashboardEditor />} />
          <Route path="/kpi/dashboards/:id/preview" element={<KpiDashboardPreview />} />

          <Route path="/" element={<Home />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
